////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Project detail route — GET /public/projects/:slug              //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useParams, Link } from 'react-router';
import { AxiosError } from 'axios';

import { useApi } from '@/hooks/useApi';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { EmptyState } from '@/components/feedback/EmptyState';
import { GlassTile } from '@/components/content/GlassTile';
import { Reveal } from '@/components/content/Reveal';

// content.projects is empty at 1.0.2 (populated later by marketing), so every slug
// 404s today per 1.0.2.md §2.4 — this route mostly proves the empty-state path. The
// real detail record shape (heroCarousel, body blocks, map) is documented in
// content/scrape/_structure/projects.md §"Suggested future API shape" for when
// there is data to render against it.
//
// MARK — 1.0.3: not in this phase's explicit named scope (1.0.3.md §4/§6 list
// About/Contact/News/Projects/Careers/Legal/NotFound; "Project detail" isn't
// named the way "News (list + detail)" is), so this route deliberately does NOT
// get its own <PageHero /> — it stays a plain heading + the same GlassTile/
// Reveal motion dialect as its sibling pages, for visual consistency without
// inventing a new hero for a route that has no real content behind it yet.
export function ProjectDetail () {
  const { slug } = useParams<{ slug: string }>();
  usePageTitle(slug ?? 'Project');

  const { status, error } = useApi<unknown>(slug ? `/public/projects/${slug}` : null);

  if (status === 'loading') {
    return <div className="mx-auto max-w-6xl px-6 py-16"><Spinner label="Loading project" /></div>;
  }

  if (status === 'error') {
    const notFound = error.cause instanceof AxiosError && error.cause.response?.status === 404;
    return (
      <div className="mx-auto max-w-6xl px-6 py-16">
        <Reveal as="div">
          {notFound ? (
            <GlassTile className="p-8 sm:p-12">
              <h1 className="text-4xl font-semibold text-ink">Project not found</h1>
              <div className="mt-4">
                <EmptyState message="Project case studies are being updated." />
              </div>
            </GlassTile>
          ) : (
            <ErrorState message="This project couldn't be loaded right now." />
          )}
        </Reveal>
        <Link to="/projects" className="mt-4 inline-block underline">Back to Projects</Link>
      </div>
    );
  }

  // The route contract guarantees a 404 for every slug while content.projects is
  // empty — a genuine 200 here has no rendering yet (no real data exists to shape it
  // against), so it degrades to the same empty state rather than a blank screen.
  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <Reveal as="div">
        <GlassTile className="p-8 sm:p-12">
          <EmptyState message="Project case studies are being updated." />
        </GlassTile>
      </Reveal>
      <Link to="/projects" className="mt-4 inline-block underline">Back to Projects</Link>
    </div>
  );
}
